import GETJwtToken from "../redux/authentication/authentication";
import {
  emitEndpointEvent,
  hasEndpointListeners,
  nextEndpointEventId,
} from "../flatbuffers/api/endpointInterceptor";

/**
 * What to export and for which organisation.
 */
export interface ExportOptions {
  orgId: string;
  stage?: number;
  includeDeps: boolean;
  serverId?: string;
  adapterId?: string;
  entityName?: string;
}

/**
 * Requests an export and hands the result to the browser as a download.
 *
 * Resolves a JWT first, so it must be awaited rather than fired and forgotten.
 */
export async function downloadExport(options: ExportOptions): Promise<void> {
  const token = await GETJwtToken();
  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE;
  const params = new URLSearchParams({
    org_id: options.orgId,
    format: "bin",
    include_deps: String(options.includeDeps),
  });
  if (options.serverId) {
    params.set("server_id", options.serverId);
  }
  if (options.adapterId) {
    params.set("adapter_id", options.adapterId);
  }

  const isFullOrg = options.stage === undefined;
  const url = isFullOrg
    ? `${baseUrl}/export/full-org/${options.orgId}?format=bin`
    : `${baseUrl}/export/stage/${options.stage}?${params.toString()}`;

  const intercepting = hasEndpointListeners();
  const startTime = intercepting ? performance.now() : 0;

  const response = await fetch(url, {
    headers: { authorization: token },
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const errJson = await response.json();
      detail = errJson?.message || errJson?.error || detail;
    } catch {
      /* response wasn't JSON */
    }
    if (intercepting) {
      emitEndpointEvent({
        id: nextEndpointEventId(),
        timestamp: Date.now(),
        method: "GET",
        url: url.replace(baseUrl || "", ""),
        status: response.status,
        duration: Math.round(performance.now() - startTime),
        requestBody: null,
        responseBody: { error: detail },
        schemaHash: null,
        typeName: "Export (BIN)",
        error: detail,
        isFlatBuffer: false,
      });
    }
    throw new Error(`Export failed: ${detail}`);
  }

  const blob = await response.blob();

  if (intercepting) {
    emitEndpointEvent({
      id: nextEndpointEventId(),
      timestamp: Date.now(),
      method: "GET",
      url: url.replace(baseUrl || "", ""),
      status: response.status,
      duration: Math.round(performance.now() - startTime),
      requestBody: null,
      responseBody: { size: blob.size, format: "bin" },
      schemaHash: null,
      typeName: "Export (BIN)",
      error: null,
      isFlatBuffer: true,
    });
  }

  const minExportSize = 300;
  if (blob.size < minExportSize) {
    throw new Error(
      `Export returned only ${blob.size} bytes — the exported data appears empty. Verify the organization has data for this stage.`,
    );
  }
  const stageLabel = isFullOrg ? "full" : `stage${options.stage}`;
  const namePrefix = options.entityName ?? `export_${stageLabel}`;
  const filename = `${namePrefix}_${Date.now()}.bin`;

  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}
